import React, { createContext, useState, useContext, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isHighSchoolSectionRoute } from '../utils/authUtils';

interface AuthModalContextType {
  loginOpen: boolean;
  signupOpen: boolean;
  openLogin: () => void;
  closeLogin: () => void;
  openSignup: () => void;
  closeSignup: () => void;
  switchToLogin: () => void;
  switchToSignup: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export const AuthModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const redirectHighSchoolToLogin = () => {
    let storedUser: any = null;
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) storedUser = JSON.parse(rawUser);
    } catch (error) {
      console.error('Unable to read the stored user before opening authentication:', error);
    }
    const isHighSchoolUser = Boolean(
      storedUser?.educationLevel === 'high-school' ||
      storedUser?.school ||
      storedUser?.schoolName ||
      storedUser?.schoolEmail ||
      storedUser?.registrationId ||
      storedUser?.className
    );
    if (!isHighSchoolUser && !isHighSchoolSectionRoute(location.pathname)) return false;
    setLoginOpen(false);
    setSignupOpen(false);
    navigate('/high-school/login', { replace: false, state: { from: location } });
    return true;
  };

  return (
    <AuthModalContext.Provider
      value={{
        loginOpen,
        signupOpen,
        openLogin: () => {
          if (redirectHighSchoolToLogin()) return;
          setLoginOpen(true);
          setSignupOpen(false);
        },
        closeLogin: () => setLoginOpen(false),
        openSignup: () => {
          if (redirectHighSchoolToLogin()) return;
          setSignupOpen(true);
          setLoginOpen(false);
        },
        closeSignup: () => setSignupOpen(false),
        switchToLogin: () => {
          if (redirectHighSchoolToLogin()) return;
          setLoginOpen(true);
          setSignupOpen(false);
        },
        switchToSignup: () => {
          if (redirectHighSchoolToLogin()) return;
          setSignupOpen(true);
          setLoginOpen(false);
        }
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
};
